import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { StreamTypes } from 'store/player/actions.types';
import { selectStartoverCapability } from 'store/user/selectors';
import { isCapabilityValid } from 'store/utils';

import parseVideoItemForSO from 'services/data-manager/parsing/parseVideoItemForSO';
import { VideoItem } from 'services/data-manager/types';

import { PlayerRef } from 'components/Player/types.ts';

import { SwitchPlayerSource } from '../types';
import { OnSkipHandler } from './useSkippings.ts';

type Params = {
    playerRef: RefObject<PlayerRef | null>;
    videoItem?: VideoItem;
    playerReady: boolean;
    isLive: boolean;
    isCatchup: boolean;
    isStartover: boolean;
    isVod: boolean;
    onSwitchPlayerSource: SwitchPlayerSource;
    handleSkip: OnSkipHandler;
    isSoDisabled: boolean;
    showSoDisabledNotification(): void;
};

function useStartover({
    playerRef,
    videoItem,
    playerReady,
    isLive,
    isCatchup,
    isStartover,
    isVod,
    handleSkip,
    onSwitchPlayerSource,
    isSoDisabled,
    showSoDisabledNotification,
}: Params) {
    const startoverCapability = useSelector(selectStartoverCapability);

    const prevPlayerReady = useRef(playerReady);

    const [startoverButtonVisible, setStartoverButtonVisible] = useState(false);

    const epgExists = !!(videoItem?.startTime && videoItem?.endTime);
    const startoverAllowed = !!(
        isCapabilityValid(startoverCapability) &&
        videoItem?.startoverAllowed &&
        (isVod || epgExists)
    );

    const handlePressStartover = useCallback(async () => {
        if (videoItem) {
            if (isLive) {
                if (isSoDisabled) {
                    showSoDisabledNotification();
                } else {
                    const sourceConfig: VideoItem['sourceConfig'] = {
                        startOffset: 0,
                        startOffsetTimelineReference: 'start',
                    };

                    onSwitchPlayerSource(
                        parseVideoItemForSO(videoItem, sourceConfig),
                        StreamTypes.startover,
                    );
                }
            } else if (
                isStartover &&
                (await playerRef.current?.getIsLive()) === false
            ) {
                handleSkip({ seekPosition: 0 });
            } else if (
                (isStartover || isCatchup) &&
                (await playerRef.current?.getIsLive())
            ) {
                const maxTimeShift = await playerRef.current?.getMaxTimeShift();

                handleSkip({
                    timeShiftOffset: maxTimeShift || videoItem?.startTime || 0,
                });
            } else {
                handleSkip({ seekPosition: 0 });
            }
        }
    }, [
        videoItem,
        isLive,
        isStartover,
        playerRef,
        isCatchup,
        isSoDisabled,
        showSoDisabledNotification,
        onSwitchPlayerSource,
        handleSkip,
    ]);

    useEffect(() => {
        if (startoverAllowed) {
            if (!prevPlayerReady.current && playerReady) {
                setStartoverButtonVisible(true);
            }

            if (!playerReady && isLive) {
                setStartoverButtonVisible(false);
            }

            prevPlayerReady.current = playerReady;
        } else {
            setStartoverButtonVisible(false);
        }
    }, [startoverAllowed, playerReady, isLive]);

    return {
        startoverAllowed,
        startoverButtonVisible,
        onPressStartover: handlePressStartover,
    };
}

export default useStartover;
